import { useState, useEffect } from 'react';
import { usersApi, casesApi } from '../lib/api';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { 
    Users, 
    UserPlus, 
    UserMinus, 
    AlertCircle,
    Loader2,
    CalendarDays,
    Check,
    X,
    Save
} from 'lucide-react';
import { getStatusColor, getStatusLabel } from '../lib/utils';
import { format, parseISO, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';

const STATUS_OPTIONS = [
    { value: 'open', label: 'APERTO', color: 'bg-[#00FF41]/20 border-[#00FF41] text-[#00FF41]' },
    { value: 'in_progress', label: 'IN CORSO', color: 'bg-yellow-400/20 border-yellow-400 text-yellow-400' },
    { value: 'closed', label: 'CHIUSO', color: 'bg-gray-400/20 border-gray-400 text-gray-400' }
];

export default function CaseAgentManager({ caseId, caseData, onUpdate }) {
    const [agents, setAgents] = useState([]);
    const [assignedAgents, setAssignedAgents] = useState(caseData.assigned_agents || []);
    const [assignedAgentsInfo, setAssignedAgentsInfo] = useState(caseData.assigned_agents_info || {});
    const [agentSchedules, setAgentSchedules] = useState(caseData.agent_schedules || {});
    const [currentStatus, setCurrentStatus] = useState(caseData.status || 'open');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // State per nuovo agente
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const [selectedDates, setSelectedDates] = useState([]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    
    // State per modifica giorni
    const [editingAgentId, setEditingAgentId] = useState(null);
    const [editingDates, setEditingDates] = useState([]);

    useEffect(() => {
        loadAgents();
    }, []);

    useEffect(() => {
        setAssignedAgents(caseData.assigned_agents || []);
        setAssignedAgentsInfo(caseData.assigned_agents_info || {});
        setAgentSchedules(caseData.agent_schedules || {});
        setCurrentStatus(caseData.status || 'open');
    }, [caseData]);

    const loadAgents = async () => {
        try {
            const response = await usersApi.listAgents();
            setAgents(response.data);
        } catch (err) {
            setError('Errore nel caricamento degli agenti');
        } finally {
            setLoading(false);
        }
    };

    const handleAssignAgent = async () => {
        if (!selectedAgentId || selectedDates.length === 0) {
            setError('Seleziona un agente e almeno un giorno di lavoro');
            return;
        }
        
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            // Converti le date in formato YYYY-MM-DD
            const dateStrings = selectedDates.map(d => format(d, 'yyyy-MM-dd'));
            
            const response = await casesApi.assignAgent(caseId, selectedAgentId, false, dateStrings);
            
            let message = 'Agente assegnato';
            if (response.data.immediate_sms_sent) {
                message += ' - SMS inviato per oggi';
            }
            if (response.data.sms_scheduled > 0) {
                message += ` (${response.data.sms_scheduled} giorni schedulati)`;
            }
            
            setSuccess(message);
            setSelectedAgentId('');
            setSelectedDates([]);
            setShowDatePicker(false);
            
            if (onUpdate) onUpdate();
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nell\'assegnazione');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveAgent = async (agentId) => {
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            await casesApi.removeAgent(caseId, agentId);
            
            setSuccess('Agente rimosso dal caso');
            
            if (onUpdate) onUpdate();
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nella rimozione');
        } finally {
            setSaving(false);
        }
    };

    const handleStartEditDates = (agentId) => {
        const schedule = agentSchedules[agentId] || {};
        const dates = (schedule.dates || []).map(d => parseISO(d));
        setEditingAgentId(agentId);
        setEditingDates(dates);
    };

    const handleCancelEditDates = () => {
        setEditingAgentId(null);
        setEditingDates([]);
    };

    const handleSaveEditDates = async () => {
        if (!editingAgentId) return;
        
        try {
            setSaving(true);
            setError('');
            
            const dateStrings = editingDates.map(d => format(d, 'yyyy-MM-dd'));
            const response = await casesApi.updateAgentSchedule(caseId, editingAgentId, dateStrings);
            
            let message = 'Giorni aggiornati';
            if (response.data.immediate_sms_sent) {
                message += ' - SMS inviato per oggi';
            }
            
            setSuccess(message);
            handleCancelEditDates();
            
            if (onUpdate) onUpdate();
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nell\'aggiornamento');
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (newStatus === currentStatus) return;
        
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            await casesApi.update(caseId, { status: newStatus });
            
            setCurrentStatus(newStatus);
            setSuccess(`Stato aggiornato a "${getStatusLabel(newStatus)}"`);
            
            if (onUpdate) onUpdate();
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nell\'aggiornamento dello stato');
        } finally {
            setSaving(false);
        }
    };

    const getAgentById = (agentId) => {
        const activeAgent = agents.find(a => a.id === agentId);
        if (activeAgent) return activeAgent;
        
        const historicInfo = assignedAgentsInfo[agentId];
        if (historicInfo) {
            return {
                id: agentId,
                full_name: historicInfo.full_name,
                email: historicInfo.email,
                fiscal_code: historicInfo.fiscal_code,
                is_deleted: true
            };
        }
        return null;
    };
    
    const availableAgents = agents.filter(a => !assignedAgents.includes(a.id) && a.is_active);

    const toggleDate = (date, dates, setDates) => {
        const exists = dates.some(d => isSameDay(d, date));
        if (exists) {
            setDates(dates.filter(d => !isSameDay(d, date)));
        } else {
            setDates([...dates, date].sort((a, b) => a - b));
        }
    };

    if (loading) {
        return (
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-[#00FF41] animate-spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0A0A0A] border border-white/10 p-4 space-y-6">
            <h3 className="font-heading text-lg text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#00FF41]" />
                Gestione Caso
            </h3>

            {error && (
                <div className="bg-red-500/10 border border-red-500 p-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                    <span className="text-red-500 text-sm">{error}</span>
                </div>
            )}

            {success && (
                <div className="bg-green-500/10 border border-green-500 p-3">
                    <span className="text-green-500 text-sm">{success}</span>
                </div>
            )}

            {/* Stato del caso */}
            <div>
                <Label className="text-white/60 text-xs uppercase mb-2 block">Stato Caso</Label>
                <div className="flex gap-2">
                    {STATUS_OPTIONS.map(option => (
                        <button
                            key={option.value}
                            onClick={() => handleStatusChange(option.value)}
                            disabled={saving}
                            className={`px-3 py-1.5 text-xs font-bold border transition-all ${
                                currentStatus === option.value
                                    ? option.color
                                    : 'bg-transparent border-white/20 text-white/40 hover:border-white/40'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Agenti assegnati */}
            <div>
                <Label className="text-white/60 text-xs uppercase mb-2 block">
                    Agenti Assegnati ({assignedAgents.length})
                </Label>
                
                {assignedAgents.length === 0 ? (
                    <p className="text-white/40 text-sm py-2">Nessun agente assegnato</p>
                ) : (
                    <div className="space-y-2">
                        {assignedAgents.map((agentId) => {
                            const agent = getAgentById(agentId);
                            const isDeleted = agent?.is_deleted;
                            const schedule = agentSchedules[agentId] || {};
                            const isEditing = editingAgentId === agentId;
                            const scheduledDates = schedule.dates || [];
                            
                            return (
                                <div
                                    key={agentId}
                                    className={`bg-black/30 border p-3 ${
                                        isDeleted ? 'border-red-500/30' : 'border-white/10'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 border flex items-center justify-center ${
                                                isDeleted 
                                                    ? 'bg-red-500/20 border-red-500/50' 
                                                    : 'bg-[#00FF41]/20 border-[#00FF41]/50'
                                            }`}>
                                                <Users className={`w-4 h-4 ${isDeleted ? 'text-red-500' : 'text-[#00FF41]'}`} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className={`font-medium ${isDeleted ? 'text-white/60' : 'text-white'}`}>
                                                        {agent?.full_name || 'Agente sconosciuto'}
                                                    </p>
                                                    {isDeleted && (
                                                        <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 uppercase font-bold">
                                                            Eliminato
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-white/50 text-xs font-mono">
                                                    {agent?.fiscal_code || '-'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {!isEditing && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleStartEditDates(agentId)}
                                                    disabled={saving}
                                                    className="text-[#00FF41] hover:bg-[#00FF41]/10 border border-[#00FF41]/30"
                                                >
                                                    <CalendarDays className="w-4 h-4 mr-1" />
                                                    Giorni
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveAgent(agentId)}
                                                disabled={saving}
                                                className="text-red-500 hover:bg-red-500/10 border border-red-500/30"
                                            >
                                                <UserMinus className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    {/* Giorni assegnati */}
                                    {!isEditing && scheduledDates.length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-white/10">
                                            <div className="flex flex-wrap gap-1">
                                                {scheduledDates.map(dateStr => (
                                                    <span 
                                                        key={dateStr}
                                                        className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 font-mono"
                                                    >
                                                        {format(parseISO(dateStr), 'dd/MM', { locale: it })}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Editor giorni */}
                                    {isEditing && (
                                        <div className="mt-3 pt-3 border-t border-white/10">
                                            <Label className="text-white/60 text-xs uppercase mb-2 block">
                                                Modifica Giorni di Lavoro
                                            </Label>
                                            <Calendar
                                                mode="multiple"
                                                selected={editingDates}
                                                onDayClick={(day, modifiers) => {
                                                    // Gestione manuale per permettere deselezionare qualsiasi giorno
                                                    const isSelected = editingDates.some(d => isSameDay(d, day));
                                                    if (isSelected) {
                                                        // Rimuovi il giorno dalla selezione
                                                        setEditingDates(editingDates.filter(d => !isSameDay(d, day)));
                                                    } else {
                                                        // Aggiungi il giorno alla selezione
                                                        setEditingDates([...editingDates, day]);
                                                    }
                                                }}
                                                locale={it}
                                                className="bg-black/50 border border-white/10 rounded-none"
                                                classNames={{
                                                    day_today: "border border-[#00FF41]/50 font-bold",
                                                    day_selected: "bg-[#00FF41] text-black hover:bg-[#00CC33] hover:text-black"
                                                }}
                                            />
                                            <div className="flex justify-end gap-2 mt-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleCancelEditDates}
                                                    disabled={saving}
                                                    className="text-white/60"
                                                >
                                                    <X className="w-4 h-4 mr-1" />
                                                    Annulla
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={handleSaveEditDates}
                                                    disabled={saving || editingDates.length === 0}
                                                    className="bg-[#00FF41] text-black"
                                                >
                                                    <Save className="w-4 h-4 mr-1" />
                                                    {saving ? '...' : 'Salva'}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Aggiungi agente */}
            {availableAgents.length > 0 && (
                <div>
                    <Label className="text-white/60 text-xs uppercase mb-2 block">
                        Assegna Nuovo Agente
                    </Label>
                    
                    <div className="space-y-3 p-3 bg-black/30 border border-white/10">
                        {/* Selezione agente */}
                        <div>
                            <Label className="text-white/40 text-xs mb-1 block">Agente</Label>
                            <select
                                value={selectedAgentId}
                                onChange={(e) => setSelectedAgentId(e.target.value)}
                                className="w-full bg-black border border-white/20 text-white p-2 text-sm focus:border-[#00FF41]"
                            >
                                <option value="">Seleziona agente...</option>
                                {availableAgents.map(agent => (
                                    <option key={agent.id} value={agent.id}>
                                        {agent.full_name} {agent.fiscal_code ? `(${agent.fiscal_code})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Selezione giorni */}
                        {selectedAgentId && (
                            <div>
                                <Label className="text-white/40 text-xs mb-1 block">
                                    Giorni di Lavoro ({selectedDates.length} selezionati)
                                </Label>
                                <Calendar
                                    mode="multiple"
                                    selected={selectedDates}
                                    onSelect={(dates) => setSelectedDates(dates || [])}
                                    locale={it}
                                    className="bg-black/50 border border-white/10 rounded-none"
                                />
                                
                                {selectedDates.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {selectedDates.map(date => (
                                            <span 
                                                key={date.toISOString()}
                                                className="text-xs bg-[#00FF41]/20 text-[#00FF41] px-2 py-0.5 font-mono"
                                            >
                                                {format(date, 'dd/MM/yyyy', { locale: it })}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Pulsante assegna */}
                        <Button
                            onClick={handleAssignAgent}
                            disabled={saving || !selectedAgentId || selectedDates.length === 0}
                            className="w-full bg-[#00FF41] text-black hover:bg-[#00CC33]"
                        >
                            <UserPlus className="w-4 h-4 mr-2" />
                            {saving ? 'Assegnazione...' : 'ASSEGNA AGENTE'}
                        </Button>
                        
                        <p className="text-white/40 text-xs">
                            ℹ️ SMS verrà inviato automaticamente alle 5:00 dei giorni selezionati
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
